import {
  CopyButton,
  DataTable,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import { WORKING_BOX } from "@/components/tools/chemistry/formula-view";
import type { DENSITY_CALCULATOR_STRINGS } from "@/components/tools/density-calculator";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  closestMaterial,
  DENSITY_UNITS,
  type DensitySolve,
  type DensityValues,
  densityFromBase,
  findDensityUnit,
  floatTest,
  WATER_4C,
} from "@/lib/tool-kit/chemistry/density";
import {
  formatPlain,
  formatSig,
  type NumberFormatter,
} from "@/lib/tool-kit/chemistry/format";
import {
  findUnit,
  fromBase,
  MASS,
  VOLUME,
} from "@/lib/tool-kit/chemistry/units";

type Strings = typeof DENSITY_CALCULATOR_STRINGS;

const ML_PER_L = 1000;

export interface DensityUnitsChosen {
  mass: string;
  volume: string;
  density: string;
}

function FloatCard({
  density,
  fluid,
  fluidName,
  strings,
  fmt,
  sig,
}: {
  density: number;
  fluid: number;
  fluidName: string;
  strings: Strings;
  fmt: NumberFormatter;
  sig: number;
}) {
  const result = floatTest(density, fluid);
  if (result === null) {
    return null;
  }
  const verdicts = {
    floats: strings.verdictFloats,
    sinks: strings.verdictSinks,
    neutral: strings.verdictNeutral,
  };
  return (
    <div className="space-y-1 rounded-lg border border-border bg-card p-4">
      <p className="font-semibold text-foreground">
        {t(verdicts[result.verdict], { fluid: fluidName })}
      </p>
      <p className="text-muted-foreground text-sm">
        {t(strings.floatDetail, {
          object: formatSig(fmt, density, sig),
          fluid: formatPlain(fmt, fluid),
        })}
        {result.verdict === "floats"
          ? ` ${t(strings.submerged, { percent: formatSig(fmt, result.submerged * 100, sig) })}`
          : ""}
      </p>
    </div>
  );
}

export function DensityResults({
  solve,
  v,
  units,
  sig,
  fluid,
  strings,
}: {
  solve: DensitySolve;
  v: DensityValues;
  units: DensityUnitsChosen;
  sig: number;
  fluid: { density: number | null; name: string };
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const massSymbol = findUnit(MASS, units.mass).symbol;
  const volumeSymbol = findUnit(VOLUME, units.volume).symbol;
  const densitySymbol = findDensityUnit(units.density).symbol;
  const mass = `${formatSig(fmt, fromBase(v.massG, MASS, units.mass), sig)} ${massSymbol}`;
  const volume = `${formatSig(
    fmt,
    fromBase(v.volumeML / ML_PER_L, VOLUME, units.volume),
    sig
  )} ${volumeSymbol}`;
  const density = `${formatSig(fmt, densityFromBase(v.density, units.density), sig)} ${densitySymbol}`;
  const answers = {
    density: { label: strings.answerDensity, value: density },
    mass: { label: strings.answerMass, value: mass },
    volume: { label: strings.answerVolume, value: volume },
  }[solve];
  const match = closestMaterial(v.density);
  const specificGravity = v.density / WATER_4C;
  const working = {
    density: t(strings.workDensity, {
      mass: formatSig(fmt, v.massG, sig),
      volume: formatSig(fmt, v.volumeML, sig),
      density: formatSig(fmt, v.density, sig + 1),
    }),
    mass: t(strings.workMass, {
      density: formatSig(fmt, v.density, sig),
      volume: formatSig(fmt, v.volumeML, sig),
      mass: formatSig(fmt, v.massG, sig + 1),
    }),
    volume: t(strings.workVolume, {
      mass: formatSig(fmt, v.massG, sig),
      density: formatSig(fmt, v.density, sig),
      volume: formatSig(fmt, v.volumeML, sig + 1),
    }),
  }[solve];
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={answers.label} value={answers.value} />
        {solve === "density" ? null : (
          <StatCard label={strings.answerDensity} value={density} />
        )}
        {solve === "mass" ? null : (
          <StatCard label={strings.answerMass} value={mass} />
        )}
        {solve === "volume" ? null : (
          <StatCard label={strings.answerVolume} value={volume} />
        )}
        <StatCard
          label={strings.specificGravity}
          value={formatSig(fmt, specificGravity, sig)}
        />
      </StatGrid>
      {match === null ? null : (
        <Notice>
          {t(strings.closeTo, {
            material:
              strings.materials[match.id as keyof typeof strings.materials],
            density: formatPlain(fmt, match.density),
          })}
        </Notice>
      )}
      {fluid.density === null ? null : (
        <FloatCard
          density={v.density}
          fluid={fluid.density}
          fluidName={fluid.name}
          fmt={fmt}
          sig={sig}
          strings={strings}
        />
      )}
      <div className={WORKING_BOX}>
        <p className="font-medium font-sans text-foreground">
          {strings.working}
        </p>
        <p>{strings.equation}</p>
        <p>{working}</p>
      </div>
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.equivalentsHeading}</h3>
        <DataTable
          head={[strings.columnUnit, strings.columnValue]}
          rows={DENSITY_UNITS.map((unit) => [
            unit.symbol,
            formatSig(fmt, densityFromBase(v.density, unit.id), sig),
          ])}
        />
      </div>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={t(strings.copyText, { mass, volume, density })}
      />
    </>
  );
}
